<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import SajuAction from './SajuAction.vue';
import SajuTooltip from './SajuTooltip.vue';

// 모바일 감지
const isMobile = ref(false);
const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768;
};

onMounted(() => {
  checkMobile();
  window.addEventListener('resize', checkMobile);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile);
});

// 모바일 접기/펼치기 상태
const isCollapsed = ref(false);

// 모바일 말풍선 툴팁 상태
const showOptionTooltip = ref(false);

const toggleOptionTooltip = () => {
  showOptionTooltip.value = !showOptionTooltip.value;
};

const closeOptionTooltip = () => {
  showOptionTooltip.value = false;
};

// Props 정의
const props = defineProps({
  formData: {
    type: Object,
    required: true,
  },
  isOptionInput: Boolean,
  isSearched: {
    type: Boolean,
    default: false,
  },
});

// Events 정의
const emit = defineEmits([
  'update:formData',
  'update:isOptionInput',
  'openTooltip',
  'resetReport',
  'search',
  'save',
  'openSummerTime',
  'optionChanged',
]);

// formData 업데이트 헬퍼
const updateFormData = (key, value) => {
  const newFormData = {
    ...props.formData,
    [key]: value,
  };
  emit('update:formData', newFormData);
  emit('optionChanged', newFormData);
};
</script>

<template>
  <!-- ========== 모바일 UI ========== -->
  <template v-if="isMobile">
    <div class="search-box right mobile-option" :class="{ collapsed: isCollapsed }">
      <!-- 모바일 옵션 헤더 -->
      <div class="mobile-option-header" @click="isCollapsed = !isCollapsed">
        <div class="mobile-option-header__left">
          <span class="mobile-option-header__title">옵션 설정</span>
          <div class="mobile-option-header__help-wrap">
            <button
              type="button"
              class="mobile-option-header__help"
              @click.stop="toggleOptionTooltip"
            >
              ?
            </button>
            <!-- 말풍선 툴팁 -->
            <div v-if="showOptionTooltip" class="mobile-option-tooltip" @click.stop="closeOptionTooltip">
              <div class="mobile-option-tooltip__content">
                <p>• 사주구성의 다양한 조건을 직접 설정하실 수 있도록 야자시/조자시, 한국시, 지역시, 동지, 입춘 옵션을 제공합니다.</p>
                <p>• 최근 조회한 옵션은 자동저장 됩니다. (회원서비스)</p>
              </div>
              <div class="mobile-option-tooltip__arrow"></div>
            </div>
            <!-- 말풍선 바깥 클릭 감지용 오버레이 -->
            <div v-if="showOptionTooltip" class="mobile-option-tooltip-overlay" @click.stop="closeOptionTooltip"></div>
          </div>
        </div>
        <button type="button" class="mobile-option-header__toggle">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            :style="{ transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)' }"
          >
            <path d="M5 12.5L10 7.5L15 12.5" stroke="#999" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>

      <!-- 모바일 옵션 내용 (펼친 상태) -->
      <div v-show="!isCollapsed" class="mobile-option-content">
        <!-- 야자시/조자시 + 한국시 적용 -->
        <div class="mobile-option-row">
          <div class="mobile-option-item saju-option-checkbox">
            <input
              id="mobile-chk0101"
              type="checkbox"
              class="checkbox"
              :checked="props.formData.option1"
              @change="updateFormData('option1', $event.target.checked)"
            />
            <label for="mobile-chk0101" class="label txt">야자시/조자시</label>
          </div>
          <div class="mobile-option-item saju-option-checkbox">
            <input
              id="mobile-kor-time"
              type="checkbox"
              class="checkbox"
              :checked="props.formData.option4"
              @change="updateFormData('option4', $event.target.checked)"
            />
            <label for="mobile-kor-time" class="label txt">한국시 적용</label>
          </div>
        </div>

        <!-- 절기 선택 (입춘/동지/모름) - PC와 동일한 라디오 스타일 사용 -->
        <div class="mobile-option-row mobile-radio-row">
          <input
            id="mobile-ra0102"
            type="radio"
            class="radio"
            name="mobile-ra01"
            :value="false"
            :checked="props.formData.option2 === false"
            @change="updateFormData('option2', false)"
          /><label for="mobile-ra0102" class="label font">입춘</label>
          <input
            id="mobile-ra0101"
            type="radio"
            class="radio"
            name="mobile-ra01"
            :value="true"
            :checked="props.formData.option2 === true"
            @change="updateFormData('option2', true)"
          /><label for="mobile-ra0101" class="label font">동지</label>
          <input
            id="mobile-ra0103"
            type="radio"
            class="radio"
            name="mobile-ra01"
            :value="null"
            :checked="props.formData.option2 === null"
            @change="updateFormData('option2', null)"
          /><label for="mobile-ra0103" class="label font">모름</label>
        </div>

        <!-- 가이드 링크 -->
        <div class="mobile-option-guides">
          <button type="button" class="mobile-guide-link" @click="emit('openTooltip', '야자시')">
            야자시/조자시?
            <span class="guide-arrow">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M4 2L7 5L4 8" stroke="#999" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </button>
          <button type="button" class="mobile-guide-link" @click="emit('openTooltip', '한국시')">
            표준시/한국시?
            <span class="guide-arrow">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M4 2L7 5L4 8" stroke="#999" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </button>
          <button type="button" class="mobile-guide-link" @click="emit('openTooltip', '절기')">
            입춘/동지?
            <span class="guide-arrow">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <path d="M4 2L7 5L4 8" stroke="#999" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </button>
        </div>
      </div>
    </div>
    <!-- 사주입력 초기화, 조회버튼 (항상 표시) -->
    <SajuAction
      :isMobile="true"
      :isSearched="props.isSearched"
      @resetReport="$emit('resetReport', $event)"
      @search="$emit('search')"
      @save="$emit('save')"
    />
  </template>

  <!-- ========== PC UI (기존) ========== -->
  <template v-else>
    <div v-if="props.isOptionInput" class="search-box right">
      <!-- 옵션 설정 헤더 -->
      <div class="option-header-row">
        <div class="option-header-row__title">옵션 설정</div>
        <button
          type="button"
          class="option-header-row__help"
          @click="emit('update:isOptionInput', false)"
        >
          ?
        </button>
      </div>

      <div class="btn-mark-box">
        <button
          class="btn-mark"
          type="button"
          @click="emit('update:isOptionInput', !props.isOptionInput)"
        >
          <span>닫기</span> <i class="i" />
        </button>
      </div>
      <div class="item-box">
        <div class="tit tit-row">
          야자시/조자시
          <SajuTooltip topic="야자시" @openTooltip="emit('openTooltip', $event)" />
        </div>
        <div class="txt saju-option-checkbox">
          <input
            id="chk0101"
            type="checkbox"
            class="checkbox"
            :checked="props.formData.option1"
            @change="updateFormData('option1', $event.target.checked)"
          />
          <label for="chk0101" class="label">체크</label>
        </div>
        <div class="tit tit-row ml-60">
          한국시 적용
          <SajuTooltip topic="한국시" @openTooltip="emit('openTooltip', $event)" />
        </div>
        <div class="txt saju-option-checkbox">
          <input
            id="kor_time"
            ref="korTimeCheckbox"
            type="checkbox"
            class="checkbox"
            :checked="props.formData.option4"
            @change="updateFormData('option4', $event.target.checked)"
          /><label class="label" for="kor_time">한국시 적용</label>
        </div>
      </div>
      <div class="item-box">
        <div class="tit tit-row">
          절기 선택
          <SajuTooltip topic="절기" @openTooltip="emit('openTooltip', $event)" />
        </div>
        <div class="txt saju-option-radio">
          <input
            id="ra0102"
            type="radio"
            class="radio"
            name="ra01"
            :value="false"
            :checked="props.formData.option2 === false"
            @change="updateFormData('option2', false)"
          /><label for="ra0102" class="label font">입춘</label>
          <input
            id="ra0101"
            type="radio"
            class="radio"
            name="ra01"
            :value="true"
            :checked="props.formData.option2 === true"
            @change="updateFormData('option2', true)"
          /><label for="ra0101" class="label font">동지</label>
          <input
            id="ra0103"
            type="radio"
            class="radio"
            name="ra01"
            :value="null"
            :checked="props.formData.option2 === null"
            @change="updateFormData('option2', null)"
          /><label for="ra0103" class="label font">모름</label>
        </div>
      </div>
      <!-- 사주입력 초기화, 조회버튼 -->
      <SajuAction @resetReport="$emit('resetReport', $event)" @search="$emit('search')" />
    </div>
    <div v-if="!props.isOptionInput" class="search-box right option">
      <!-- 옵션 설정 헤더 (요약 설명 모드) -->
      <div class="option-header-row">
        <div class="option-header-row__title">옵션 설정</div>
        <button
          type="button"
          class="option-header-row__help"
          @click="emit('update:isOptionInput', true)"
        >
          ?
        </button>
      </div>

      <div class="btn-mark-box">
        <button
          class="btn-mark"
          type="button"
          @click="emit('update:isOptionInput', !props.isOptionInput)"
        >
          <span>옵션</span> <i class="i" />
        </button>
      </div>
      <ul class="opt-list">
        <li>
          • 사주구성의 다양한 조건을 직접 설정하실 수 있도록 <br />야자시/조자시, 한국시, 지역시,
          동지, 입춘 옵션을 제공합니다.
        </li>
        <li>• 최근 조회한 옵션은 자동저장 됩니다. (회원서비스)</li>
        <li>
          • 1948년부터 1988년까지의
          <span class="line-btm pop" @click="emit('openSummerTime')">서머타임 적용 기간 표</span>
          를 제공합니다.
        </li>
      </ul>
      <!-- 사주입력 초기화, 조회버튼 -->
      <SajuAction @resetReport="$emit('resetReport', $event)" @search="$emit('search')" />
    </div>
  </template>
</template>
